import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, useMediaQuery, useTheme } from '@mui/material';

import MultiColorPicker from '~/components/atoms/color-picker/MultiColorPicker';
import TemplateSlider from '~/components/atoms/template-preview/TemplateSlider';
import Accordion from '~/components/ui/Accordion/Accordion';
import Select from '~/components/ui/Select';
import { DESKTOP_MIN_WIDTH, FONTS } from '~/constants/common';
import { useAppDispatch } from '~/hooks/useRedux';
import CreateQRContentBlockLayout from '~/pages/CreateQR/components/CreateQRContentBlockLayout/CreateQRContentBlockLayout';
import { listOfLinksSlice } from '~/store/reducers/previewScreens/list-of-links';
import { updateListOfLinksSiteThunk } from '~/store/reducers/previewScreens/list-of-links';
import { getListOfLinksReducer } from '~/store/reducers/previewScreens/list-of-links/selectors';

import { templatesImages } from '../utils';

const { actions } = listOfLinksSlice;

export default function Appearance() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const theme = useTheme();

  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));
  const listOfLinksState = useSelector(getListOfLinksReducer);

  const fontsSizeLabels = [
    { label: t('template2:titleSize'), values: ['12px', '14px', '16px', '18px', '20px'], id: 'titleSize' },
    {
      label: t('template2:descriptionSize'),
      values: ['10px', '12px', '14px', '16px', '18px', '20px'],
      id: 'descriptionSize',
    },
  ];
  const { selectedTemplate, fontsSize, micrositeData, fonts, bacgkroundColor } = listOfLinksState;

  const getReplacedInitialColor = () => {
    if (bacgkroundColor) return bacgkroundColor;
  };

  const setColorValues = (val) => {
    if (micrositeData?.id) {
      dispatch(
        updateListOfLinksSiteThunk({
          id: micrositeData.id,
          body: {
            design: {
              primary: val[0],
              secondary: val[1],
              tertiary: val[2],
            },
          },
          dispatchHandler: () => dispatch(actions.setBackgroundColor(val)),
        }),
      );
    }
  };

  const setFontStyles = (e, field) => {
    const value = e.target.value;

    dispatch(
      updateListOfLinksSiteThunk({
        id: micrositeData.id,
        body: {
          fonts: { ...fonts, ...fontsSize, [field]: value },
        },
        dispatchHandler: () => dispatch(actions.setFonts({ field, value })),
      }),
    );
  };

  const setFontSizes = (e, field) => {
    const value = e.target.value;

    dispatch(
      updateListOfLinksSiteThunk({
        id: micrositeData.id,
        body: {
          fonts: { ...fonts, ...fontsSize, [field]: value },
        },
        dispatchHandler: () => dispatch(actions.setFontsSize({ field, value })),
      }),
    );
  };

  const handleChangeTemplate = (val) => {
    dispatch(
      updateListOfLinksSiteThunk({
        id: micrositeData.id,
        body: {
          selectedTemplate: val,
        },
        dispatchHandler: () => dispatch(actions.setSelectedTemplate(val)),
      }),
    );
  };

  return (
    <CreateQRContentBlockLayout type="appearance">
      <Accordion
        defaultExpanded
        variant="child"
        summary={t('basicTemplateLabels:tmplPreview')}
        details={
          <TemplateSlider
            defaultSelectedItem={selectedTemplate}
            selectSlide={handleChangeTemplate}
            images={templatesImages}
          />
        }
      />
      <Accordion
        defaultExpanded
        variant="child"
        summary={t('basicTemplateLabels:tmplDesign')}
        details={
          <MultiColorPicker
            initialColors={[
              ['#FFFCE8', '#ffffff', '#EDDC6A'],
              // ['#5356FF', '#ffffff', '#000000'],
              ['#BA68C8', '#FFFFFF', '#7B1FA2'],
              ['#EF5350', '#FFFFFF', '#C62828'],
              ['#FF9800', '#FFFFFF', '#EF6C00'],
            ]}
            selectedColors={[
              micrositeData?.design?.primary,
              micrositeData?.design?.secondary,
              micrositeData?.design?.tertiary,
            ]}
            selectedTemplate={selectedTemplate}
            setColorValues={setColorValues}
            onSketchPickerChange={(val) => dispatch(actions.setBackgroundColor(val))}
            getReplacedInitialColor={getReplacedInitialColor}
            blurFunction={(arr) => setColorValues(arr)}
          />
        }
      />
      <Accordion
        defaultExpanded
        variant="child"
        hideBottomBorder
        summary={t('basicTemplateLabels:tmplFonts')}
        details={
          <>
            <Box display="flex" flexDirection={isDesktop ? 'row' : 'column'} sx={{ gap: '16px' }}>
              <Select
                items={FONTS}
                onChange={(e) => setFontStyles(e, 'title')}
                fullWidth
                label={t('other:title')}
                defaultValue={fonts?.title || FONTS[3].value}
              />
              <Select
                items={FONTS}
                onChange={(e) => setFontStyles(e, 'text')}
                fullWidth
                label={t('other:text')}
                defaultValue={fonts?.text || FONTS[3].value}
              />
            </Box>

            <Box display="flex" flexDirection={isDesktop ? 'row' : 'column'} sx={{ gap: '16px', marginTop: '12px' }}>
              {fontsSizeLabels.map((item, index) => {
                return (
                  <Select
                    items={item.values.map((value) => ({ label: value, value }))}
                    onChange={(e) => setFontSizes(e, item.id)}
                    key={index}
                    fullWidth
                    label={item.label}
                    value={fontsSize[item.id]}
                  />
                );
              })}
            </Box>
          </>
        }
      />
    </CreateQRContentBlockLayout>
  );
}
